import { useEffect } from 'react';

import { getNativePlatform } from '#features/notifications/utils/nativePlatform';
import { envConfig } from '#constants/envConfig';

const VIEWPORT_FIT = 'viewport-fit=cover';

/**
 * Opts the iOS shell into drawing under the notch and the home indicator.
 *
 * Without `viewport-fit=cover` iOS reports every `env(safe-area-inset-*)` as 0, which
 * silently disables all the safe-area padding already written across the app —
 * BottomNavigation, MainLayout, ChatInput, CallOverlay. The tab bar then sits on top of
 * the home indicator and content runs under the notch. Nothing errors; the padding is
 * simply never there, which is why this went unnoticed while only Android was built.
 *
 * iOS only, on purpose. Android's WebView is not edge-to-edge here, so the attribute buys
 * it nothing — and that build is on its way to Play, so it must not change. Web is
 * tree-shaken out by IS_NATIVE, and would in any case be a real layout change on mobile
 * Safari rather than a no-op.
 *
 * Applied to the live tag instead of rendered in <head>: the SPA shell is prerendered on
 * Node, where the platform is always 'web', so a rendered attribute would bake in the
 * wrong value and fight hydration.
 */
export const useNativeViewportFit = () => {
  useEffect(() => {
    if (!envConfig.IS_NATIVE) {
      return;
    }

    let active = true;
    let restore: (() => void) | undefined;

    (async () => {
      if ((await getNativePlatform()) !== 'ios' || !active) {
        return;
      }

      // Scope shell styles to iOS without baking the prerenderer's 'web' platform into HTML.
      const root = document.documentElement;
      const previousPlatform = root.getAttribute('data-native-platform');
      root.setAttribute('data-native-platform', 'ios');
      restore = () => {
        if (previousPlatform === null) root.removeAttribute('data-native-platform');
        else root.setAttribute('data-native-platform', previousPlatform);
      };

      const meta = document.querySelector('meta[name="viewport"]');
      const previous = meta?.getAttribute('content');

      if (!meta || previous === null || previous === undefined || previous.includes(VIEWPORT_FIT)) {
        return;
      }

      meta.setAttribute('content', `${previous}, ${VIEWPORT_FIT}`);
      const restorePlatform = restore;
      restore = () => {
        meta.setAttribute('content', previous);
        restorePlatform();
      };
    })();

    return () => {
      active = false;
      restore?.();
    };
  }, []);
};
